import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import OlMap from 'ol/Map';
import { environment } from '../../../environments/environment';
import WMSCapabilities from 'ol/format/WMSCapabilities.js';
import { unByKey } from 'ol/Observable';
import Overlay from 'ol/Overlay';
import { getArea, getLength } from 'ol/sphere';
import { LineString, Polygon } from 'ol/geom';
import Draw from 'ol/interaction/Draw';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { DoubleClickZoom, DragPan, KeyboardPan } from 'ol/interaction';
import { transform } from 'ol/proj.js';
import { primaryAction } from 'ol/events/condition';
import { DisplayService } from '../../services/display.service';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppText, GeometryTypes, Languages, mapLayer } from 'src/app/interfaces/common';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import { LayerService } from 'src/app/services/layer.service';
import { EventsKey } from 'ol/events';
import { EditFeatureService } from '../query-views/services/edit-feature.service';

enum MeasureUnit {
  Metric = "metric",
  Nautical = "nautical",
  Angle = "angle",
  Polygon = "polygon"
}

let draw: Draw | undefined = undefined;
let measuring = false;
let cancel = false;

const gudnaBlar = "#4e8ecc";
const lmiGrarTransparent = "rgba(	147, 149, 152, 0.5)";
const MAX_UI_TIMES = 50;  // Max number of time steps to show in the timeline to prevent it from becoming unreadably dense

@Component({
  selector: 'app-map-tools',
  templateUrl: './map-tools.component.html',
  styleUrls: ['./map-tools.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class MapToolsComponent implements OnInit {
  @Input() mapObject : OlMap = new OlMap();
  @Input() isInMeasure: boolean = false;
  @Input() selText : AppText = {};
  @Input() secondaryMapObject: OlMap = new OlMap();
  @Input() lang: Languages = Languages.Icelandic;
  @Input() mapLayers: mapLayer[] = [];

  timeLayerSelected: mapLayer | undefined = undefined;
  selectedTimeLayerPointer: number = 0;
  selectedTimeLayerPointerFloat: number = 0;
  frameRate = 1.0;
  smoothSlider = false;
  playSpeed = 1.0;
  displayLatestDataFirst = false;
  animationId: any = 0;
  sliderRafId: number = 0;
  isMoving: boolean = false;
  showDynamicLayerList:boolean = false;
  allowSpeedControl: boolean = false;
  measureOverlays: Overlay[] = [];
  pointerEvent: string = 'pointermove';
  clickEvent: string = 'click';
  pointerMoveHandler: any;
  measureToolsUsed: boolean = false;
  measureDistance_visable: boolean = false;
  measureToolsUnits: boolean = false;
  measureAreaVisable: boolean = false;
  measureToolsVisableChange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  measureToolsVisable: boolean = false;
  sessionURL_visableChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  sessionURL_visable: boolean = false;
  timeLayers: mapLayer[] = [];
  splitTimeLayers: mapLayer[] = [];
  rightSideTimeSelected:boolean = false;
  currentTimesLength: number = 3;
  currentTime: string = '';
  lastUsedTimePointers: any = [];
  MeasureUnit = MeasureUnit;

  constructor(
    public displayService: DisplayService,
    private layerService: LayerService,
    private editFeatureService: EditFeatureService,
    private changeDetector: ChangeDetectorRef,
    private http: HttpClient
  ) { }
  
  ngOnInit(): void {
    this.timeLayers = this.getTimeLayers();
    this.splitTimeLayers = this.getSplitTimeLayers();
    
    if(this.timeLayers.length > 0){
      this.timeLayerSelected = this.mapLayers.find(x => x.name == this.timeLayers[0].name);
      this.getTimesFromCapabilities(this.timeLayerSelected);
      this.displayService.isLayerTimelineVisible$.next(true);
      this.applyInitialTimeSelection(this.timeLayerSelected?.option?.times, this.timeLayerSelected);
      this.mapObject.render();
    }

    this.measureToolsUsed = this.displayService.measureTools_used;
    this.measureDistance_visable = this.displayService.measureDistance_visable;
    this.measureToolsUnits = this.displayService.measureTools_units;
    this.measureAreaVisable = this.displayService.measureArea_visable;
    this.measureToolsVisableChange$ = this.displayService.getMeasureToolsVisable();
    this.measureToolsVisable = this.measureToolsVisableChange$.value;
    
    // Subscription for updates
    this.measureToolsVisableChange$.subscribe((newVal: boolean) => {
      this.measureToolsVisable = newVal;
      this.changeDetector.markForCheck(); // Marking for check and rerender, measureToolsVisable determines if measure tools are open(visible) or closed(not visible)
    })

    this.sessionURL_visableChange = this.displayService.getSessionURLVisable();
    this.sessionURL_visable = this.sessionURL_visableChange.value;
    this.sessionURL_visableChange.subscribe( (newVal: boolean) => {
      this.sessionURL_visable = newVal;
      this.changeDetector.markForCheck();
    })

    this.currentTime = this.getCurrentTime();
    
    // Fetch dynamic timelayers on 'moveend'
    this.mapObject.on('moveend', () => {
      if (this.timeLayerSelected != undefined && this.timeLayerSelected.option.isDynamic && this.getTimeLayers().length > 0) {
        const options = this.displayService.getAppSettings().applications.find((x: any) => x.application = this.displayService.getAppSettings()['application']);
        const postData = {
          "application": this.displayService.getAppSettings()['application'],
          "layer": this.timeLayerSelected['id'],
          "bbox": this.mapObject.getView().calculateExtent(this.mapObject.getSize()),
          "bbox_epsg": options.option.map['epsg']
        };
        this.getDynamicLayers(postData)
          .then(() => {
            this.getTimesLength(MAX_UI_TIMES);
    
            // if current time is not in new time, reset current time and reset index
            if (this.timeLayerSelected?.option.times.indexOf(this.currentTime) == -1) {
              let temp_name = '';
              if (this.rightSideTimeSelected) temp_name = '_right';
              const lup = this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name));
              const findingLayer = this.timeLayerSelected.option.times.indexOf(lup["lastUsedTime"]);
              if (findingLayer == -1) this.selectedTimeLayerPointer = this.getInitialTimePointerIndex(this.timeLayerSelected?.option?.times);
              else this.selectedTimeLayerPointer = this.timeLayerSelected.option.times.indexOf(this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name))["lastUsedTime"]);
              this.selectedTimeLayerPointerFloat = this.selectedTimeLayerPointer;
              this.currentTime = this.timeLayerSelected.option.times[this.selectedTimeLayerPointer];
              this.updateSource(this.selectedTimeLayerPointer);
            }
            // If current time is in new time, keep current time but reset index
            else {
              this.selectedTimeLayerPointer = this.timeLayerSelected?.option.times.indexOf(this.currentTime);
              this.selectedTimeLayerPointerFloat = this.selectedTimeLayerPointer;
              this.updateSource(this.selectedTimeLayerPointer);
            }
            this.changeDetector.markForCheck();
          })
      }
    });

    /*
      Does not really need the mc object since this component checks through all of mapLayers.
      But it does need to know when something has changed in mapLayers which this does here.
    */
    this.layerService.getNewMaplayersStatus().subscribe(() => {
      let hadTimeLayers = this.timeLayers.length > 0;
      const prevTimeLayers = this.timeLayers;
      this.timeLayers = this.getTimeLayers();
      this.splitTimeLayers = this.getSplitTimeLayers();

      // When a new time layer is added alongside existing ones, switch focus to it.
      // Air-quality layers (SO₂, NO₂, PM₁₀) are mutually exclusive; wind can coexist with any of them.
      // TODO: Don't use hardcoded values for AIR_QUALITY_LAYERS, instead use config from db
      const AIR_QUALITY_LAYERS = ['so2Data', 'no2Data', 'pm10Data'];
      if (this.timeLayers.length > 1) {
        const newLayer = this.timeLayers.find(l => !prevTimeLayers.some(p => p.id === l.id));
        if (newLayer) {
          if (AIR_QUALITY_LAYERS.includes(newLayer.name)) {
            // Turn off any other air-quality layer that was already on (leave wind alone)
            const toUncheck = prevTimeLayers.filter(l => AIR_QUALITY_LAYERS.includes(l.name) && l.id !== newLayer.id);
            if (toUncheck.length > 0) {
              toUncheck.forEach(l => { l.checked = false; });
              this.layerService.setLayerChecked({ layers: toUncheck, newCheckStatus: false, isSecondaryMap: false });
              this.timeLayers = this.getTimeLayers();
            }
          }
          this.timeLayerSelected = newLayer;
          hadTimeLayers = false; // trigger reset so slider starts at 0 for the new layer
        }
      }

      // Need this here or timeLayer won't work if it was not checked from the start
      if(this.timeLayers.length > 0 && !this.rightSideTimeSelected) {
        this.timeLayerSelected = this.mapLayers.find(x => x.name == this.timeLayers[0].name);
        this.getTimesFromCapabilities(this.timeLayerSelected);
        this.displayService.isLayerTimelineVisible$.next(true);
        // If the layer was just turned on from scratch, reset to a clean initial state
        if (!hadTimeLayers) {
          this.applyInitialTimeSelection(this.timeLayerSelected?.option?.times, this.timeLayerSelected);
          this.isMoving = false;
          clearInterval(this.animationId);
          if (this.sliderRafId) { cancelAnimationFrame(this.sliderRafId); this.sliderRafId = 0; }
          this.setMapPanEnabled(true);
          this.showDynamicLayerList = false;
          this.displayService.isLayerTimelineVisible$.next(true);
        }
        this.updateSource(this.selectedTimeLayerPointer);
        this.mapObject.render();
      }
      else if(this.splitTimeLayers.length > 0 && this.rightSideTimeSelected) {
        this.timeLayerSelected = this.mapLayers.find(x => x.name == this.splitTimeLayers[0].name);
        this.getTimesFromCapabilities(this.timeLayerSelected);
        this.displayService.isLayerTimelineVisible$.next(true);
        this.updateSource(this.selectedTimeLayerPointer);
        this.mapObject.render();
      }
      if (this.timeLayers.length === 0 && this.splitTimeLayers.length === 0) this.displayService.isLayerTimelineVisible$.next(false)
      this.changeDetector.markForCheck()
    })
  }
  
  /*
    Possibly dead code. Dynamic times are fetched from getCapabilities.
    Only possible use for this is when dynamic times are needed but LMI data can't be public in getCapabilities().
  */
  getDynamicLayers(postData: any): Promise<any> {
    return new Promise((resolve,reject) => {
      this.http.post(environment.bendURL + '/gr/time', JSON.stringify(postData)).subscribe({
        next: (res: any) => {
          res.features.sort((a: any, b: any) => (a.properties.defdate < b.properties.defdate ? -1 : 1));  // Put layers in timeorder
          let times = res.features.map((a: any) => a.properties.defdate); // Extract times into a single timearray
          times = times.filter((elem: any, index: number, self: any[]) => index === self.indexOf(elem));  // Remove duplicates

          //Replace available times
          if (this.timeLayerSelected && this.timeLayerSelected.option) {
          this.timeLayerSelected.option.times = times;
            if (!times.includes(this.currentTime)) this.currentTime = times[0];
          }
          resolve(res);
        },
        error: err => {
          reject(err);
        }
      })
    })
  }

  // Get time stamps for layer from their getCapabilities endpoint, this makes the timeline data more dynamic and we won't have to update our db everytime new data is available.
  getTimesFromCapabilities(layer: mapLayer | undefined): void {
    const tl = layer?.option?.mp_layer?.timeline;
    this.frameRate = tl?.tlFrameRate ?? 1.0;
    this.smoothSlider = tl?.tlSmoothSlider === true;
  
    if (layer?.option?.displayLatestDataFirst !== undefined) this.displayLatestDataFirst = layer.option.displayLatestDataFirst;
    else this.displayLatestDataFirst = false;
  
    // Custom layers populate option.times before returning — skip WMS GetCapabilities fetch
    if (layer?.option?.times?.length > 0) {
      this.applyInitialTimeSelection(layer?.option?.times, layer);
      this.getTimesLength(MAX_UI_TIMES);
      this.changeDetector.markForCheck();
      return;
    }

    const parser = new WMSCapabilities();
    const dS = this.displayService;
    let serv_str = '?service=';
    if (layer?.url.includes("?")) serv_str = '&service=';
  
    const getCapabilitiesURL = layer?.url + serv_str + layer?.type + '&request=GetCapabilities';
    fetch(getCapabilitiesURL)
      .then((response) => response.text())
      .then((text: any) => {
        const result = parser.read(text);
        const tempLayer = result?.Capability?.Layer?.Layer?.find((x: any) => x.Name == layer?.name);
        if (tempLayer !== undefined) {
          if (tempLayer.Dimension !== undefined && layer) {
            let tempTimes = tempLayer.Dimension.find((x: any) => x.name == 'time');
            tempTimes = tempTimes.values;
            layer.option.times = tempTimes.split(',');
            this.applyInitialTimeSelection(layer.option?.times, layer);
            this.getTimesLength(MAX_UI_TIMES);
          }
        }
        else if (result?.Capability?.Layer?.Layer[0]?.Layer !== undefined) {
          const dimensions = result?.Capability?.Layer?.Layer[0]?.Layer?.find((y: any) => y.Name == layer?.name).Dimension.find((d: any) => d.name == 'time').values;
  
          const times = dS.generateTimes(dimensions, layer?.option);
          if (times.length > 0 && layer) {
            layer.option.times = times;
            this.applyInitialTimeSelection(layer.option?.times, layer);
            this.getTimesLength(MAX_UI_TIMES);
          }
        }
  
        if (this.rightSideTimeSelected) this.changeTimeLayer(this.splitTimeLayers[0].id, this.rightSideTimeSelected);
        else this.changeTimeLayer(this.timeLayers[0].id, this.rightSideTimeSelected);
  
        if (this.displayLatestDataFirst) {
          this.selectedTimeLayerPointer = layer?.option.times.length - 1;
          this.selectedTimeLayerPointerFloat = this.selectedTimeLayerPointer;
          this.updateSource(this.selectedTimeLayerPointer);
        }
  
        this.currentTime = this.getCurrentTime();
        this.changeDetector.markForCheck();
      });
  }  

  getSpeedControl(timeLayer: mapLayer | undefined): boolean {
    if (timeLayer?.option?.mp_layer?.timeline?.allowSpeedControl == true) return true;
    else return false;
  }
  
  initMeasure(measureType: string, unit: string): void {
    measuring = true;
    cancel = false;

    const drawingLayer: VectorLayer<VectorSource> = this.layerService.getDrawingLayerOL();
    const source = drawingLayer.getSource();
    if (!source) return;

    let sketch: Feature<Geometry> | undefined;  // Currently drawn feature

    // HTML Elements:
    let measureTooltipElement: HTMLElement | null;
    let helpTooltipElement: HTMLElement | null;

    // Overlays:
    let measureTooltip: Overlay;
    let helpTooltip: Overlay;

    const mapproj = ''.concat("EPSG:", this.displayService.epsg);
    const mapObject = this.mapObject;
    const measureOverlays = this.measureOverlays;

    const formatLength = (line: Geometry) => {
      const length = getLength(line);
      let output;
      if (unit == MeasureUnit.Nautical) output = (Math.round(length / 1852 * 100) / 100) + ' ' + this.selText.nauticalmileShort;
      else if (unit == MeasureUnit.Angle) {
        const coords = (line as LineString).getCoordinates();

        if (coords.length === 2) {
          const c1 = transform(coords[0], mapproj, 'EPSG:4326');
          const c2 = transform(coords[1], mapproj, 'EPSG:4326');

          const lon1 = c1[0] * Math.PI / 180;
          const lat1 = c1[1] * Math.PI / 180;
          const lon2 = c2[0] * Math.PI / 180;
          const lat2 = c2[1] * Math.PI / 180;

          const dLon = lon2 - lon1;

          const y = Math.sin(dLon) * Math.cos(lat2);
          const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

          let bearing = Math.atan2(y, x);
          bearing = (bearing * 180 / Math.PI + 360) % 360;

          return bearing.toFixed(3) + '°';
        }
        return '';
      }
      else {
        if (length > 999) output = (Math.round(length / 1000 * 100) / 100) + ' ' + 'km';
        else output = (Math.round(length * 100) / 100) + ' ' + 'm';
        if (this.lang == Languages.Icelandic) output = output.replace(".", ",");
      } 
      return output;
    };

    const formatArea = (polygon: Geometry): string => {
      const area = getArea(polygon);
      let output;
      if (area > 9999) output = (Math.round(area / 1000000 * 100) / 100) + ' ' + 'km<sup>2</sup>';
      else output = (Math.round(area * 100) / 100) + ' ' + 'm<sup>2</sup>';
      if (this.lang == Languages.Icelandic) output = output.replace(".", ",");
      return output;
    };

    const displayserv = this.displayService;

    const addInteraction = (measureType: any) => {
      const type = measureType;
      if(unit == MeasureUnit.Angle) {
        draw = new Draw({
          source: source,
          maxPoints: 2,
          type: type,
          condition: (e) => primaryAction(e),
          style: new Style({
            fill: new Fill({
              color: lmiGrarTransparent
            }),
            stroke: new Stroke({
              color: gudnaBlar,
              lineDash: [10, 10],
              width: 2
            }),
            image: new CircleStyle({
              radius: 5,
              stroke: new Stroke({
                color: gudnaBlar
              }),
              fill: new Fill({
                color: gudnaBlar
              })
            })
          })
        });
      }
      else {
        draw = new Draw({
          source: source,
          type: type,
          style: new Style({
            fill: new Fill({
              color: lmiGrarTransparent
            }),
            stroke: new Stroke({
              color: gudnaBlar,
              lineDash: [10, 10],
              width: 2
            }),
            image: new CircleStyle({
              radius: 5,
              stroke: new Stroke({
                color: gudnaBlar
              }),
              fill: new Fill({
                color: gudnaBlar
              })
            })
          })
        });
      }

      mapObject.addInteraction(draw);
      createMeasureTooltip();
      createHelpTooltip.call(this);
      let listener: EventsKey | undefined;
      
      draw.on('drawstart', (evt: any) => {
        let dblClickInteraction;
        mapObject.getInteractions().getArray().forEach(function(interaction) {
          if (interaction instanceof DoubleClickZoom) dblClickInteraction = interaction;
        });
        if (dblClickInteraction) mapObject.removeInteraction(dblClickInteraction);

        // Set sketch
        sketch = evt.feature;
        /** @type {import("../src/ol/coordinate.js").Coordinate|undefined} */
        let tooltipCoord = evt.coordinate;
  
        listener = sketch?.getGeometry()?.on('change', function(evt: any) {
          const geom = evt.target;
          let output;
          if (geom instanceof Polygon) {
            output = formatArea(geom);
            tooltipCoord = geom.getInteriorPoint().getCoordinates();
          }
          else if (geom instanceof LineString) {
            output = formatLength(geom);
            tooltipCoord = geom.getLastCoordinate();
          }
          if (!cancel && measureTooltipElement !== null) {
            measureTooltipElement.innerHTML = output !== undefined && output !== null ? output : '';
            measureTooltipElement?.setAttribute("style","background-color:rgba(255, 255, 255, 0.5);border:1px solid black;padding:5px;border-radius:10px 10px;");
            measureTooltip.setPosition(tooltipCoord);
          }
        });
      });
    
      draw.on('drawend', () => {
        if (!cancel) {
          measuring = false;
          if (measureTooltipElement !== null) measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
          measureTooltipElement?.setAttribute("style","background-color:rgba(255, 255, 255, 0.5);border:1px solid black;padding:5px;border-radius:10px 10px;");
          measureTooltip.setOffset([0, -7]);
        }
        sketch = undefined;
        measureTooltipElement = null;

        if (draw) mapObject.removeInteraction(draw);
        if (!cancel) createMeasureTooltip();
        if (listener) unByKey(listener);

        displayserv.setReopenSearch(true);
        document.getElementById("mapid")?.focus({preventScroll: true});
        draw = undefined;
        measuring = false;
      });
    };
    
    function createHelpTooltip(this: MapToolsComponent) {
      if (helpTooltipElement) helpTooltipElement.parentNode?.removeChild(helpTooltipElement);
      helpTooltipElement = document.createElement('div');
      helpTooltipElement.className = 'ol-tooltip hidden';
      helpTooltip = new Overlay({
        element: helpTooltipElement,
        offset: [15, 0],
        positioning: 'center-left'
      });
      mapObject.addOverlay(helpTooltip);
      measureOverlays.push(helpTooltip);
    }

    function createMeasureTooltip() {
      if (measureTooltipElement) measureTooltipElement.parentNode?.removeChild(measureTooltipElement);
      measureTooltipElement = document.createElement('div');
      measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
      measureTooltip = new Overlay({
        element: measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center',
        stopEvent:false
      });
      mapObject.addOverlay(measureTooltip);
      measureOverlays.push(measureTooltip);
    }
    addInteraction(measureType);
  }

  clearMeasures(): void {
    if (!measuring) {
      this.layerService.clearDrawingLayerSource(this.mapObject);
      draw = undefined;
    }
    else {
      cancel = true;
      draw?.finishDrawing();
      this.clearMeasures();
    }
  }

  getCurrentTime(): string {
    if (this.timeLayerSelected == undefined || (this.timeLayers.find(x => x.id == this.timeLayerSelected?.id) == undefined && this.splitTimeLayers.find(x => x.id == this.timeLayerSelected?.id) == undefined)) {
      if (this.timeLayers.length > 0) {
        if (this.rightSideTimeSelected) this.timeLayerSelected = this.splitTimeLayers[0];
        else this.timeLayerSelected = this.timeLayers[0];
        this.getTimesFromCapabilities(this.timeLayerSelected);
      }
      else return '';
    }
    const time = this.timeLayerSelected.option.times[this.selectedTimeLayerPointer];
    return this.prettifyTimes(time);
  }

  private getInitialTimePointerIndex(times: Array<string | undefined> | undefined, layer: mapLayer | undefined = this.timeLayerSelected): number {
    if (!times?.length) return 0;

    const shouldStartAtCurrentDate = layer?.option?.mp_layer?.timeline?.startSliderCurrentTime === true;

    if (!shouldStartAtCurrentDate) return 0;

    const now = Date.now();

    const parsedTimes = times
      .map((time, index) => ({
        index,
        timestamp: Date.parse(time ?? '')
      }))
      .filter(t => !Number.isNaN(t.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (!parsedTimes.length) return 0;

    const nextFutureIndex = parsedTimes.findIndex(t => t.timestamp >= now); // Find first future time

    if (nextFutureIndex !== -1) return parsedTimes[nextFutureIndex].index;  // If we have future times, use first future

    return parsedTimes[0].index;  // Everything is in the past, so start at first
  }

  private applyInitialTimeSelection(times: Array<string | undefined> | undefined, layer: mapLayer | undefined = this.timeLayerSelected): void {
    if (!layer) return;

    this.timeLayerSelected = layer;
    const index = this.getInitialTimePointerIndex(times, layer);
    this.selectedTimeLayerPointer = index;
    this.selectedTimeLayerPointerFloat = index;
    this.currentTime = this.getCurrentTime();
    this.updateSource(this.selectedTimeLayerPointer);
    this.changeDetector.markForCheck();
  }

  prettifyTimes(time: string | undefined): string {
    if (time === undefined) return '';

    const [datePart, timePart] = time.split('T');
    if (this.timeLayerSelected?.option.displayTimeFormat == 'y') return datePart.split('-')[0];
    if (this.timeLayerSelected?.option.displayTimeFormat == 'ym') {
      const spl = datePart.split('-');
      return spl.slice(0, 2).join('-');
    }

    if (timePart === undefined) return datePart;

    const timeMatch = timePart.match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      const [, hour, minute] = timeMatch;
      return `${datePart} ${hour}:${minute}`;
    }

    return `${datePart} ${timePart.replace(/Z$/, '')}`;
  }
  
  // Update the timeline after user clicks on it
  // Smoothly animate the slider thumb from its current float position to toInt over ~90% of one interval.
  // If tlSmoothSlider is false the thumb snaps immediately.
  private animateSlider(toInt: number): void {
    if (this.sliderRafId) { cancelAnimationFrame(this.sliderRafId); this.sliderRafId = 0; }
    if (!this.smoothSlider) {
      this.selectedTimeLayerPointerFloat = toInt;
      this.changeDetector.markForCheck();
      return;
    }
    const from = this.selectedTimeLayerPointerFloat;
    const duration = 1250 * this.frameRate * 0.9;
    const startTime = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1.0);
      this.selectedTimeLayerPointerFloat = from + (toInt - from) * t;
      this.changeDetector.markForCheck();
      if (t < 1.0) {
        this.sliderRafId = requestAnimationFrame(animate);
      } else {
        this.selectedTimeLayerPointerFloat = toInt;
        this.sliderRafId = 0;
      }
    };
    this.sliderRafId = requestAnimationFrame(animate);
  }

  private setMapPanEnabled(enabled: boolean): void {
    this.mapObject.getInteractions().getArray()
      .filter(i => i instanceof DragPan || i instanceof KeyboardPan)
      .forEach(i => i.setActive(enabled));
  }

  changeVal(value: any): void {
    clearInterval(this.animationId);
    if (this.sliderRafId) { cancelAnimationFrame(this.sliderRafId); this.sliderRafId = 0; }
    this.isMoving = false;
    this.setMapPanEnabled(true);

    const intValue = Math.round(Number(value));
    if (intValue >= this.timeLayerSelected?.option.times.length - 1) clearInterval(this.animationId);
    
    this.updateSource(intValue);
    this.mapObject.render();
    this.selectedTimeLayerPointer = intValue;
    this.selectedTimeLayerPointerFloat = intValue;
    document.getElementById("mapid")?.focus({preventScroll: true});
    if (this.displayService.splitMapActive$.value) document.getElementById("secondaryMap")?.focus({ preventScroll: true });
    this.currentTime = this.getCurrentTime()  // Change displayed date
  }

  changeBackground(elements: string[], color: string): void {
    for (let i = 0; i < elements.length; i++) {
      const element = document.getElementById(elements[i]);
      if (element) element.style.backgroundColor = color;
    }
  }

  updateSource(value: any): void {
    if(!this.isSelectedLayerActive()){
      if (this.rightSideTimeSelected) this.changeTimeLayer(this.splitTimeLayers[0].id, this.rightSideTimeSelected);
      else this.changeTimeLayer(this.timeLayers[0].id, this.rightSideTimeSelected);
    }

    // Custom layers expose a setTimeStep() method instead of WMS TIME params
    const olLayer = this.timeLayerSelected?.layer;
    if (olLayer && typeof (olLayer as any).setTimeStep === 'function') {
      (olLayer as any).setTimeStep(this.timeLayerSelected?.option.times[value]);
      return;
    }

    let temp_arr = [];
    if (this.rightSideTimeSelected) {
      temp_arr = this.secondaryMapObject.getLayers().getArray().filter((x: any) => x.getSource().params_ != undefined && x.getSource().params_['LAYERS'] == this.timeLayerSelected?.name);      
    }
    else {
      temp_arr = this.mapObject.getLayers().getArray().filter((x: any) => x.getSource().params_ != undefined && x.getSource().params_['LAYERS'] == this.timeLayerSelected?.name);
    }
    
    if(temp_arr.length == 1){
      const params = { 'TIME': this.timeLayerSelected?.option.times[value] };
      (temp_arr[0] as any).getSource().updateParams(params);
    }
    else {
      // Filter by style
      const temp = temp_arr.find((x: any) => x.getSource().params_['STYLES'] == this.timeLayerSelected?.style);
      const params = { 'TIME': this.timeLayerSelected?.option.times[value] };
      (temp as any)?.getSource()?.updateParams(params);
    }
  }

  // Set up the actions to move the time tick
  playTime(): void {
    if (this.isMoving) {
      this.isMoving = false;
      clearInterval(this.animationId);
      this.setMapPanEnabled(true);
    }
    else {
      this.isMoving = true;
      if (this.timeLayers.find(x => x.id == this.timeLayerSelected?.id) == undefined && !this.rightSideTimeSelected) {
        this.timeLayerSelected = this.timeLayers[0];
        this.getTimesFromCapabilities(this.timeLayerSelected);
      }
      else if (this.splitTimeLayers.find(x => x.id == this.timeLayerSelected?.id) == undefined && this.rightSideTimeSelected) {
        this.timeLayerSelected = this.splitTimeLayers[0];
        this.getTimesFromCapabilities(this.timeLayerSelected);
      }

      if (this.selectedTimeLayerPointer >= this.timeLayerSelected?.option.times.length - 1) {
        this.selectedTimeLayerPointer = 0;
        this.selectedTimeLayerPointerFloat = 0;
        this.updateSource(this.selectedTimeLayerPointer);
        this.currentTime = this.getCurrentTime();
      }

      this.setMapPanEnabled(false);
      this.moveTime();  // Fire immediately so the slider starts moving at once instead of waiting one full interval
      this.changeDetector.markForCheck();
      this.animationId = setInterval(() => {
        this.moveTime();
        this.changeDetector.markForCheck();
      }, 1250 * this.frameRate);
    }
    document.getElementById("mapid")?.focus({preventScroll: true});
    if (this.displayService.splitMapActive$.value) document.getElementById("secondaryMap")?.focus({ preventScroll: true });
  }

  // Do the actions needed to move tick and update image
  moveTime(): void {
    this.selectedTimeLayerPointer = Number(this.selectedTimeLayerPointer);  // Make sure pointer is number and not string
    if (!this.isMoving || this.timeLayers.length == 0 || this.selectedTimeLayerPointer >= this.timeLayerSelected?.option.times.length - 1 || this.timeLayers.find(x => x.id == this.timeLayerSelected?.id) == undefined) {
      clearInterval(this.animationId);
      this.isMoving = false;
      this.setMapPanEnabled(true);
    }
    else {
      this.selectedTimeLayerPointer += 1;
      this.animateSlider(this.selectedTimeLayerPointer);
      this.updateSource(this.selectedTimeLayerPointer);
      this.currentTime = this.getCurrentTime();
    }
    this.mapObject.render();
  }

  changeFramerate(lower: boolean): void {
    if (lower && this.playSpeed > 0.25) this.playSpeed -= 0.25;
    else if (!lower && this.playSpeed < 3) this.playSpeed += 0.25;
    this.frameRate = 1 / this.playSpeed;

    // Pause -> Play or Play -> Pause, but still update the correct sources and pointers
    this.playTime();
    this.playTime();
  }

  changeTimeLayer(id: number, isRightSideTimeSelected: boolean): void {
    let temp_name = '';
    if (this.rightSideTimeSelected) temp_name = '_right';
    if (this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name)) == undefined) {
      this.lastUsedTimePointers.push({ 'name': this.timeLayerSelected?.name.concat(temp_name), "lastUsedTime": 0 });
    }
    else {
      if (this.timeLayerSelected?.option?.times?.length > 0) {
        this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name))["lastUsedTime"] = 0;
      }
      else {
        this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name))["lastUsedTime"] = this.timeLayerSelected?.option.times[this.selectedTimeLayerPointer];
      }
    }

    this.rightSideTimeSelected = isRightSideTimeSelected;
    this.timeLayerSelected = this.mapLayers.find(x => x.id == id);
    if (this.timeLayerSelected?.option.times.length == 0) this.getTimesFromCapabilities(this.timeLayerSelected);

    // Re-featch if dynamic layer since times might have changed since last fetch
    if (this.timeLayerSelected?.option.isDynamic) {
      const options = this.displayService.getAppSettings().applications.find((x: any) => x.application = this.displayService.getAppSettings()['application']);
      const postData = {
        "application":this.displayService.getAppSettings()['application'],
        "layer":this.timeLayerSelected['id'],
        "bbox":this.mapObject.getView().calculateExtent(this.mapObject.getSize()),
        "bbox_epsg":options.option.map['epsg']
      }
      this.getDynamicLayers(postData).then(() => {
        this.getTimesLength(MAX_UI_TIMES);
        
        // If current time is not in new time, reset current time and reset index
        if (this.timeLayerSelected?.option.times.indexOf(this.currentTime) == -1) {
          const lup = this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name));
          const findingLayer = lup != undefined ? this.timeLayerSelected?.option.times.indexOf(lup["lastUsedTime"]) : -1;
          if (findingLayer == -1) this.selectedTimeLayerPointer = this.getInitialTimePointerIndex(this.timeLayerSelected?.option?.times);
          else this.selectedTimeLayerPointer = this.timeLayerSelected.option.times.indexOf(this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name))["lastUsedTime"]);
          this.selectedTimeLayerPointerFloat = this.selectedTimeLayerPointer;
          this.currentTime = this.timeLayerSelected.option.times[this.selectedTimeLayerPointer];
          this.updateSource(this.selectedTimeLayerPointer);
        }
        // Else if current time is in new time, keep current time but reset index
        else {
          this.selectedTimeLayerPointer = this.timeLayerSelected?.option.times.indexOf(this.currentTime);
          this.updateSource(this.selectedTimeLayerPointer);
        }
        this.changeDetector.markForCheck();
      })
    }
    this.allowSpeedControl = this.getSpeedControl(this.timeLayerSelected);
    const lup = this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name));
    const findingLayer = lup != undefined ? this.timeLayerSelected?.option.times.indexOf(lup["lastUsedTime"]) : -1;
    if (findingLayer == -1){
      if (this.displayLatestDataFirst) this.selectedTimeLayerPointer = this.timeLayerSelected?.option.times.length - 1;
      else this.selectedTimeLayerPointer = this.getInitialTimePointerIndex(this.timeLayerSelected?.option?.times);
    }
    else {
      this.selectedTimeLayerPointer = this.timeLayerSelected?.option.times.indexOf(this.lastUsedTimePointers.find((x: any) => x.name == this.timeLayerSelected?.name.concat(temp_name))["lastUsedTime"]);
    }
    this.currentTime = this.getCurrentTime();
    
    this.animationId= 0;
    this.getTimesLength(MAX_UI_TIMES);
    document.getElementById("mapid")?.focus({preventScroll: true});
    if (this.displayService.splitMapActive$.value) document.getElementById("secondaryMap")?.focus({ preventScroll: true });
  }

  isSelectedLayerActive(): boolean {
    return this.timeLayers.some(x => x.id === this.timeLayerSelected?.id) || this.splitTimeLayers.some(x => x.id === this.timeLayerSelected?.id);
  }

  getTimeLayers(): mapLayer[] {
    const timelayers = this.mapLayers.filter(x => x.checked && x.option != null && x.option.hasOwnProperty('times'));
    return (timelayers);
  }

  getSplitTimeLayers(): mapLayer[] {
    const splitTimeLayers = this.layerService.secondaryMapLayers.filter(x => x.layer != null && x.option != null && x.option.hasOwnProperty('times'));
    return (splitTimeLayers);
  }
  getAllTimeLayers(): mapLayer[] {
    const timelayers = this.mapLayers.filter(x => x.option != null && x.option.hasOwnProperty('times'));
    return (timelayers);
  }

  /*
    Get the number of displayable times in a layer. If > max, return max.
    This is for the html markup to know how many ticks it should display.
  */
  getTimesLength(max: number): void {
    const temp = this.timeLayerSelected?.option?.times?.length ?? 3;
    this.currentTimesLength = Math.min(Math.max(temp, 3), max);
  }

  // Show or hide the timeplayer
  flipTimePlayer(): void {
    this.displayService.isLayerTimelineVisible$.next(!this.displayService.isLayerTimelineVisible$.value);
    document.getElementById("mapid")?.focus({preventScroll: true});
  }

  // Show or hide the list of times
  flipDynamicLayerList(): void {
    this.showDynamicLayerList = !this.showDynamicLayerList;
    document.getElementById("mapid")?.focus({preventScroll: true});
  }

  measureClicked(unit: MeasureUnit): void {
    // Need to cancel if for some reason user is currently creating new feature because then the measure will interfer with the feature
    this.editFeatureService.cancelNewFeature();
    this.layerService.getDrawingLayerOL().setStyle(this.layerService.getDefaultVectorDrawStyle());
    if (!measuring) {
      this.displayService.disallowFeatureClick();
      if (unit === MeasureUnit.Angle) this.measureAngleClicked();
      else if (unit === MeasureUnit.Polygon) this.initMeasure(GeometryTypes.Polygon, MeasureUnit.Metric);
      else this.initMeasure(GeometryTypes.LineString, unit);
    }
    else {
      cancel = true;
      draw?.finishDrawing();
      this.measureClicked(unit);
    }
  }

  measureAngleClicked(): void {
    /* 
      Check if mobile and then use 'pointerup' event instead of 'pointermove'.
      The 'pointermove' event is not supported for touch devices.
    */
    if (window.ontouchstart !== undefined) this.pointerEvent = "pointerup";
    this.initMeasure(GeometryTypes.LineString, MeasureUnit.Angle);
  }

  flipVisibility(): void {
    this.editFeatureService.setSelectInteractionActive(true);
    this.clearMeasures();
    this.displayService.allowFeatureClick();
    this.displayService.flipMeasureToolsVisable();
  }

  getSessionURL(): string {
    return this.displayService.getSessionURL();
  }

  closeSessionURL(): void {
    this.displayService.closeSessionURL();
  }

  turnOffLayer(x: mapLayer): void {
    this.layerService.setLayerChecked({ layers: [x], newCheckStatus: false, isSecondaryMap: this.rightSideTimeSelected });
  }

  getDisplayTitle(layer: mapLayer): string {
    return (layer.option?.mp_layer?.legend?.title ?? layer.title ?? layer.name ?? '');
  }
}